import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { MaskedReveal, Reveal, BirdReveal, FallingLeaves, BranchDivider, WindReveal, useReducedMotion } from "@/components/Cinematic";

const PROCESS_STEPS = [
  { k: "Seed", d: "We plant the strategic intent." },
  { k: "Roots", d: "Research grounds every decision." },
  { k: "Growth", d: "Identity & systems take shape." },
  { k: "Bloom", d: "The brand meets the world." },
  { k: "Harvest", d: "Measurable business outcomes." },
];

function Hero() {
  const { scrollYProgress } = useScroll();
  const reduced = useReducedMotion();
  const y = useTransform(scrollYProgress, [0, 0.3], [0, reduced ? 0 : 120]);
  return (
    <section className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-16"
      data-testid="hero-section">
      <FallingLeaves />
      <BirdReveal />
      <motion.div style={{ y }} className="mx-auto w-full max-w-[1400px] px-6 lg:px-10 relative z-10">
        <motion.p initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          data-testid="hero-tagline"
          className="text-xs sm:text-sm uppercase tracking-[0.28em] text-[#66371B] mb-4">
          Brand. Strategy. Design.
        </motion.p>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25 }}
          className="signature text-2xl sm:text-3xl text-[#81754B] mb-6">
          A brand & creative studio
        </motion.p>
        <MaskedReveal
          testId="hero-headline"
          className="font-serif text-[13vw] sm:text-[11vw] lg:text-[9rem] leading-[0.86] tracking-tight text-[#3F3F2C]"
          lines={["Design that", <em key="i" className="signature text-[#66371B]">remembers</em>, "where it came from."]}
        />
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1 }}
          className="mt-10 max-w-xl text-lg text-[#3F3F2C] leading-relaxed">
          We help founders turn premium D2C, fashion and lifestyle ideas into brands worth remembering — built to convert, crafted to last.
        </motion.p>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3 }}
          className="mt-10 flex flex-wrap items-center gap-5">
          <Link to="/contact" data-testid="hero-cta-button"
            className="group rounded-full bg-[#3F3F2C] px-8 py-4 text-[#F7F1E1] hover:bg-[#66371B] transition-colors inline-flex items-center gap-2">
            Let's create <ArrowUpRight size={18} className="group-hover:rotate-45 transition-transform" />
          </Link>
          <Link to="/work" data-testid="hero-work-link"
            className="link-underline text-[#3F3F2C] uppercase tracking-[0.15em] text-sm">
            View selected work
          </Link>
        </motion.div>
      </motion.div>
    </section>
  );
}

function FeaturedWork({ projects }) {
  const wantedSlugs = ["itti", "myntra-taavi", "w-fashion-design"];
  const featured = wantedSlugs
    .map((slug) => projects.find((p) => p.slug === slug))
    .filter(Boolean);
  const spans = ["lg:col-span-6 lg:row-span-2", "lg:col-span-6", "lg:col-span-6"];
  return (
    <section className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24 lg:py-32" data-testid="featured-work">
      <div className="flex items-end justify-between mb-14">
        <div>
          <p className="text-sm uppercase tracking-[0.2em] text-[#66371B] mb-3">Featured</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#3F3F2C]">
            <WindReveal text="Recent work, in brief." />
          </h2>
        </div>
        <Link to="/work" className="hidden md:inline-flex items-center gap-2 text-sm uppercase tracking-[0.15em] link-underline text-[#3F3F2C]" data-testid="featured-all-link">
          All work <ArrowRight size={16} />
        </Link>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 auto-rows-[280px]">
        {featured.map((p, i) => (
          <Reveal key={p.slug} delay={i * 0.06} className={`group relative overflow-hidden rounded-sm bg-[#E3D8C1] ${spans[i]}`}>
            <a href={p.behance_url || "https://www.behance.net/pravidhijain"} target="_blank" rel="noreferrer" data-testid={`featured-project-${p.slug}`} className="block h-full w-full">
              <img src={p.hero_image} alt={p.title} loading="lazy"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#3F3F2C]/85 via-[#3F3F2C]/10 to-transparent" />
              <div className="absolute bottom-0 p-7 text-[#F7F1E1]">
                <p className="text-xs uppercase tracking-[0.2em] text-[#E3D8C1]/80 mb-2">{p.categories?.[0]}</p>
                <h3 className="font-serif text-2xl lg:text-3xl">{p.title}</h3>
                <p className="text-sm text-[#E3D8C1]/90 mt-1">{p.subtitle}</p>
              </div>
              <span className="absolute top-5 right-5 grid place-items-center h-10 w-10 rounded-full bg-[#F7F1E1]/90 text-[#3F3F2C] opacity-0 -translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all">
                <ArrowUpRight size={18} />
              </span>
            </a>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function ImpactStrip({ metrics }) {
  if (!metrics?.length) return null;
  const doubled = [...metrics, ...metrics, ...metrics];
  return (
    <section className="border-y border-[#E3D8C1] bg-[#E3D8C1]/50 py-8 overflow-hidden" data-testid="impact-strip">
      <div className="flex gap-14 marquee-track whitespace-nowrap w-max">
        {doubled.map((m, i) => (
          <div key={i} className="flex items-baseline gap-4">
            <span className="font-serif text-3xl lg:text-4xl text-[#B4833D]">{m.value}</span>
            <span className="text-xs uppercase tracking-[0.2em] text-[#3F3F2C]">{m.label}</span>
            <span className="text-[#81754B] text-2xl" aria-hidden="true">·</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function ProcessTeaser() {
  return (
    <section className="bg-[#E3D8C1]/60 py-24 lg:py-32" data-testid="process-teaser">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <p className="text-sm uppercase tracking-[0.2em] text-[#66371B] mb-3">Process</p>
        <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#3F3F2C] mb-16">A brand grows like a tree.</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
          {PROCESS_STEPS.map((s, i) => (
            <Reveal key={s.k} delay={i * 0.08} className="relative">
              <div className="h-px w-full bg-[#81754B]/40 mb-5" />
              <p className="font-serif text-3xl text-[#3F3F2C]">{s.k}</p>
              <p className="text-sm text-[#81754B] mt-2">{s.d}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const [data, setData] = useState({ featured: [], services: [], testimonials: [], clients: [], metrics: [] });
  useEffect(() => {
    document.title = "seedesign — Brand. Strategy. Design.";
    Promise.all([
      api.get("/projects"),
      api.get("/services"), api.get("/testimonials"), api.get("/clients"), api.get("/impact-metrics"),
    ]).then(([p, s, t, c, m]) =>
      setData({ featured: p.data, services: s.data, testimonials: t.data, clients: c.data, metrics: m.data })
    ).catch(() => {});
  }, []);

  return (
    <div>
      <Hero />
      <ImpactStrip metrics={data.metrics} />
      <FeaturedWork projects={data.featured} />
      <BranchDivider flip flowers />
      <ProcessTeaser />
    </div>
  );
}
